// Battery Sizing Tool: field metadata + client-side validation, used for instant inline
// feedback as the user types. The actual sizing math and the authoritative (server-side)
// validation live in the main backend — see src/lib/toolsApi.js (batteryToolApi),
// backend/tools/battery_sizing_tool.py and backend/core/lookup/battery_specs.py (which
// this file mirrors — keep the two in sync).

export const CONSUMPTION_BASES = [
  {
    value: 'battery',
    label: 'Battery-side (measured)',
    help: 'Wh/km drawn from the battery, as measured on a drive cycle or quoted on a spec sheet. Drivetrain losses and auxiliary loads are already inside this figure, so they are not added again.',
  },
  {
    value: 'wall',
    label: 'Wall-side (EPA / WLTP)',
    help: 'Wh/km measured at the charger, as EPA and WLTP ratings are. The tool removes charging losses to get the battery-side figure.',
  },
  {
    value: 'wheel',
    label: 'Wheel-side (tractive)',
    help: 'Tractive energy at the wheels, e.g. from a road-load calculation. The tool adds driveline losses and auxiliary energy, and subtracts regen recovery.',
  },
];

export const DEFAULT_INPUTS = {
  consumptionBasis: 'battery',
  rangeKm: 250,
  consumptionWhKm: 150,
  drivelineEffPct: 90,
  regenRecoveryPct: 0,
  avgSpeedKmph: 46.5,
  auxPowerW: 500,
  chargingEffPct: 88,
  packVoltageV: 400,
  motorContKw: 50,
  motorPeakKw: 100,
  motorInvEffPct: 92,
  cellNomV: 3.7,
  cellMinV: 3.0,
  cellMaxV: 4.2,
  cellCapacityAh: 50,
  cRate: 2,
  pulseCRate: 4,
  cellMassKg: 0.9,
  cellToPackMassPct: 65,
  minSocPct: 10,
  maxSocPct: 90,
  eolCapacityPct: 80,
  coldDeratePct: 0,
};

// min / max are inclusive bounds; strictlyPositive additionally rejects 0.
// `bases` limits a field to the consumption bases that use it (hidden otherwise).
export const FIELD_SPECS = [
  { key: 'rangeKm', label: 'Target Range', unit: 'km', min: null, max: 2000, strictlyPositive: true },
  { key: 'consumptionWhKm', label: 'Energy Consumption', unit: 'Wh/km', min: null, max: 1000, strictlyPositive: true },
  { key: 'packVoltageV', label: 'Target Pack Voltage', unit: 'V', min: 12, max: 1000, strictlyPositive: false },
  { key: 'auxPowerW', label: 'Auxiliary Power', unit: 'W', min: 0, max: 10000, strictlyPositive: false,
    help: 'HVAC, 12 V loads, pumps. ~500 W with no A/C; 2–5 kW with cabin heating. Always used in the current check; added to energy only on the wheel-side basis.' },
  { key: 'chargingEffPct', label: 'Charging Efficiency', unit: '%', min: 70, max: 100, strictlyPositive: false, bases: ['wall'],
    help: 'Wall-to-battery AC charging efficiency, typically 85–90%.' },
  { key: 'drivelineEffPct', label: 'Driveline Efficiency', unit: '%', min: 50, max: 99, strictlyPositive: false, bases: ['wheel'],
    help: 'Battery-to-wheel average over the trip (inverter + motor + gearbox). Drive-cycle averages are often 78–88%.' },
  { key: 'regenRecoveryPct', label: 'Regen Recovery', unit: '%', min: 0, max: 50, strictlyPositive: false, bases: ['wheel'],
    help: 'Share of tractive energy returned by regenerative braking. ~15–25% urban, ~5% highway, 0 to be conservative.' },
  { key: 'avgSpeedKmph', label: 'Average Trip Speed', unit: 'km/h', min: 5, max: 200, strictlyPositive: false, bases: ['wheel'],
    help: 'Converts auxiliary power into energy (trip time = range ÷ speed). WLTC ≈ 46.5, urban ≈ 20–30, highway ≈ 90–110.' },
  { key: 'motorContKw', label: 'Motor Continuous Power', unit: 'kW', min: null, max: 1000, strictlyPositive: true },
  { key: 'motorPeakKw', label: 'Motor Peak Power', unit: 'kW', min: null, max: 2000, strictlyPositive: true },
  { key: 'motorInvEffPct', label: 'Motor + Inverter Efficiency', unit: '%', min: 50, max: 99, strictlyPositive: false,
    help: 'At the continuous / peak operating point. Converts motor shaft power into battery power.' },
  { key: 'cellNomV', label: 'Cell Nominal Voltage', unit: 'V', min: 1.5, max: 4.5, strictlyPositive: false },
  { key: 'cellMinV', label: 'Cell Min Voltage', unit: 'V', min: 1.0, max: 4.5, strictlyPositive: false },
  { key: 'cellMaxV', label: 'Cell Max Voltage', unit: 'V', min: 1.5, max: 5.0, strictlyPositive: false },
  { key: 'cellCapacityAh', label: 'Cell Capacity', unit: 'Ah', min: null, max: 2000, strictlyPositive: true },
  { key: 'cRate', label: 'Continuous C-Rate', unit: 'C', min: null, max: 20, strictlyPositive: true },
  { key: 'pulseCRate', label: 'Pulse (Peak) C-Rate', unit: 'C', min: null, max: 50, strictlyPositive: true,
    help: 'Datasheet 10–30 s pulse discharge rating.' },
  { key: 'cellMassKg', label: 'Cell Mass', unit: 'kg', min: null, max: 20, strictlyPositive: true },
  { key: 'cellToPackMassPct', label: 'Cell-to-Pack Mass Ratio', unit: '%', min: 30, max: 100, strictlyPositive: false,
    help: 'Cell mass as a share of total pack mass. Module-based packs ~60–70%, cell-to-pack designs ~75–85%.' },
  { key: 'minSocPct', label: 'Min SOC', unit: '%', min: 0, max: 50, strictlyPositive: false },
  { key: 'maxSocPct', label: 'Max SOC', unit: '%', min: 50, max: 100, strictlyPositive: false },
  { key: 'eolCapacityPct', label: 'End-of-Life Capacity', unit: '%', min: 50, max: 100, strictlyPositive: false,
    help: 'State of health at which the range target must still be met. 70–80% is typical for warranty end-of-life; 100% sizes a new pack only.' },
  { key: 'coldDeratePct', label: 'Cold-Weather Capacity Loss', unit: '%', min: 0, max: 50, strictlyPositive: false,
    help: 'Usable capacity lost at your cold design temperature, ~10–20% near −10 °C. 0 = no derating.' },
];

export const SOC_PRESETS = [
  { label: 'Conservative 10–90%', min: 10, max: 90 },
  { label: 'Modern OEM 5–95%', min: 5, max: 95 },
];

export function isFieldActive(spec, basis) {
  return !spec.bases || spec.bases.includes(basis);
}

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}

// Cross-field rules, mirroring backend/tools/battery_sizing_tool.py. Mutates `errors`.
export function validateCrossFields(values, errors) {
  const ok = (...keys) => keys.every((k) => !errors[k]);
  if (ok('minSocPct', 'maxSocPct') && values.maxSocPct <= values.minSocPct) {
    errors.minSocPct = 'Min SOC must be less than Max SOC.';
    errors.maxSocPct = 'Max SOC must be greater than Min SOC.';
  }
  if (ok('cellMinV', 'cellMaxV') && values.cellMaxV <= values.cellMinV) {
    errors.cellMinV = 'Cell Min Voltage must be less than Cell Max Voltage.';
    errors.cellMaxV = 'Cell Max Voltage must be greater than Cell Min Voltage.';
  }
  if (ok('cellNomV', 'cellMinV', 'cellMaxV') && !(values.cellMinV < values.cellNomV && values.cellNomV < values.cellMaxV)) {
    errors.cellNomV = 'Cell Nominal Voltage must lie between Cell Min and Cell Max Voltage.';
  }
  if (ok('motorContKw', 'motorPeakKw') && values.motorContKw > values.motorPeakKw) {
    errors.motorContKw = 'Continuous power cannot exceed peak power.';
    errors.motorPeakKw = 'Peak power must be at least the continuous power.';
  }
  if (ok('cRate', 'pulseCRate') && values.pulseCRate < values.cRate) {
    errors.cRate = 'Continuous C-rate cannot exceed the pulse C-rate.';
    errors.pulseCRate = 'Pulse C-rate must be at least the continuous C-rate.';
  }
  return errors;
}
